import { Link, useNavigate } from "react-router-dom";
import { auth } from "../config/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { signOut } from "firebase/auth";
import { useEffect, useRef, useState } from "react";
import InboxDrawer from "./inbox";
import SettingsDrawer from "./settings";
import { useStealthMode } from "../context/StealthModeContext";
import useSignoutUser from "../services/useSignout";
import { useAppSelector } from "../redux/reduxHook/useAuthActions";
import {
  collection,
  onSnapshot,
  orderBy,
  query,
  where,
  getDocs,
  limit,
} from "firebase/firestore";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../config/firebase";

export const Navbar = () => {
  const [user] = useAuthState(auth);
  const [rows, setRows] = useState([]);
  const [openPanel, setOpenPanel] = useState(null);
  const [unseenMessageUsers, setUnseenMessageUsers] = useState(0);
  const navigate = useNavigate();

  const { enabled, toggle } = useStealthMode();

  const signoutUser = useSignoutUser();

  const threadListeners = useRef({});

  const userUid = user?.uid || null;

  useEffect(() => {
    if (!userUid) return;

    // Track unsubscribers for each thread
    const threadMap = {};
    const threadsQuery = query(
      collection(db, "messages"),
      where("participants", "array-contains", userUid),
      orderBy("updatedAt", "desc"),
      limit(20)
    );

    const unsubThreads = onSnapshot(threadsQuery, (snapshot) => {
      snapshot.docChanges().forEach((change) => {
        const threadId = change.doc.id;

        if (["added", "modified"].includes(change.type)) {
          // Already listening to this thread
          if (threadMap[threadId]) return;

          const msgsQuery = query(
            collection(db, "messages", threadId, "msg"),
            orderBy("createdAt", "desc"),
            limit(20)
          );

          // Listen to individual thread messages
          const unsubMsg = onSnapshot(msgsQuery, (msgSnap) => {
            let hasUnread = false;

            msgSnap.forEach((msgDoc) => {
              const data = msgDoc.data();

              if (!data.read && data.sender !== userUid) {
                hasUnread = true;
              }
            });

            // Mark thread as having unread or not
            threadMap[threadId] = hasUnread;

            // Count how many threads have unread messages
            const unreadUserCount =
              Object.values(threadMap).filter(Boolean).length;

            // Set state
            setUnseenMessageUsers(unreadUserCount);
          });

          threadMap[threadId] = false;
          threadListeners.current[threadId] = unsubMsg;
        }
      });
    });

    return () => {
      unsubThreads();
      Object.values(threadListeners.current).forEach((u) => u());
      threadListeners.current = {};
    };
  }, [userUid, setUnseenMessageUsers]);

  const logOut = async () => {
    try {
      setOpenPanel(null);
      // await signOut(auth);
      await signoutUser();
      navigate("/login", { replace: true });
    } catch (err) {
      console.error(err);
    }
  };

  const isCorrupted = useAppSelector((state) => state.auth.isCorrupted);

  if (isCorrupted) {
    return null;
  }

  return (
    <>
      <nav className="primary-menu">
        <div className="main-navigation">
          <a href="https://flintt.me/" className="mobile-hide">
            <div className="nav-logo"></div>
          </a>

          {user ? (
            <ul className="menu-ul">
              <li>
                <div className="nav-icon nav-profile"></div>
                {/* <Link to={`/profile/${user.uid}`}> */}
                <button
                  className="mobile-icon nav-profile"
                  aria-label="Profile"
                  onClick={() => {
                    setOpenPanel(null);
                    navigate(`/profile/${user.uid}`);
                  }}
                >
                  Profile
                </button>
                {/* </Link> */}
              </li>
              <li>
                <div className="nav-icon nav-discover"></div>
                {/* <Link to={`/discovery`}> */}
                <button
                  className="mobile-icon nav-discover"
                  aria-label="Discover"
                  onClick={() => {
                    setOpenPanel(null);
                    navigate(`/discovery`);
                  }}
                >
                  Discover
                </button>
                {/* </Link> */}
              </li>
              <li>
                {/* <div className="nav-icon nav-messages"></div> */}
                <div className="nav-icon-wrapper">
                  <div className="nav-icon nav-messages"></div>

                  {unseenMessageUsers > 0 && unseenMessageUsers < 100 ? (
                    <span className="nav-badge">{unseenMessageUsers}</span>
                  ) : unseenMessageUsers >= 100 ? (
                    <span
                      className="nav-badge"
                      style={{ width: "8px", height: "8px", padding: 0 }}
                    ></span>
                  ) : null}
                </div>
                <button
                  className="mobile-icon nav-messages"
                  aria-label="Messages"
                  type="button"
                  onClick={() =>
                    setOpenPanel((p) => (p === "messages" ? null : "messages"))
                  }
                >
                  Messages
                </button>
              </li>

              <li>
                <div className="nav-icon nav-account"></div>
                <button
                  className="mobile-icon nav-account"
                  aria-label="Account settings"
                  type="button"
                  onClick={() =>
                    setOpenPanel((p) => (p === "settings" ? null : "settings"))
                  }
                >
                  Account
                </button>
              </li>

              <li>
                <div className="nav-icon nav-premium"></div>
                {/* <Link to={`/pricing`}> */}
                <div className="mobile-padding">
                  <button
                    className="mobile-icon nav-premium"
                    aria-label="Try Premium"
                    onClick={() => {
                      setOpenPanel(null);
                      navigate(`/pricing`);
                    }}
                  >
                    Try Premium
                  </button>
                </div>
                {/* </Link> */}
              </li>
              <li>
                <div
                  className={
                    enabled
                      ? "nav-icon nav-stealth-show"
                      : "nav-icon nav-stealth"
                  }
                ></div>
                <button
                  onClick={toggle}
                  aria-pressed={enabled}
                  className={
                    enabled
                      ? "mobile-icon nav-stealth-show stealth-button"
                      : "mobile-icon nav-stealth stealth-button"
                  }
                >
                  {enabled ? "Disable Stealth" : "Enable Stealth"}
                </button>
              </li>
              <li className="logout-li">
                <button
                  onClick={logOut}
                  className="logout-button"
                  aria-label="Sign out"
                >
                  {" "}
                  Sign Out{" "}
                </button>
              </li>
            </ul>
          ) : (
            <ul>
              <li className="login-link">
                <Link to="/login">Login</Link>
              </li>
              <li className="signup-link">
                <Link to="/register">Sign Up</Link>
              </li>
              <li className="referral-link">
                <Link to="/referral">Refer a friend</Link>
              </li>
            </ul>
          )}
        </div>
      </nav>
      {/* SECONDARY DRAWER */}
      {openPanel === "messages" && user && (
        <InboxDrawer user={user} onClose={() => setOpenPanel(null)} />
      )}
      {/* SECONDARY DRAWER */}
      {openPanel === "settings" && user && (
        <SettingsDrawer user={user} onClose={() => setOpenPanel(null)} />
      )}
    </>
  );
};
